import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { faFileDownload } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box, Button, LinearProgress, Typography } from '@mui/material';

import { claimQueries } from '@repo/api/claim/claims';
import { EIntervalType } from '@repo/api/claim/claims.types';
import { HTTPError } from '@repo/api/client';
import { downloadExtractSalesLoss } from '@repo/api/shared/generics';
import { useUser } from '@repo/auth';
import useCurrency from '@repo/features/hooks/useCurrency';
import { downloadFile } from '@repo/ui/utils/download';

import { ErrorMessage } from '../-components/ErrorMessage';
import { DailySalesLossTable } from './-components/DailySalesLossTable';
import { MonthlySalesLossTable } from './-components/MonthlySalesLossTable';
import { SalesLossContentComingSoon } from './-components/SalesLossContentComingSoon';
import { WeeklySalesLossTable } from './-components/WeeklySalesLossTable';

type ErrorData = {
	propertyErrors: {
		errorMessage: string;
		propertyName: string;
	}[];
};

const notImplementedMessage =
	'Monthly Sales Loss responses have not been implemented. Use Daily or Weekly EPOI for Sales Loss results.';

export const Route = createFileRoute(
	'/_claims/claim/$claimId/_claim/calculations/salesLoss',
)({
	component: function SalesLossCalculationsPage() {
		const { claimId } = Route.useParams();
		const { data: claim } = useSuspenseQuery(claimQueries.id(claimId));
		const {
			data: { salesPeriodData } = {},
			isPending,
			error,
		} = useQuery(claimQueries.salesLoss(claimId));
		const { permissions } = useUser();
		const canExtractSalesLoss = permissions.includes(
			'claim.sales.loss.extract',
		);
		const currency = useCurrency(claim.claimInsured.countryId);

		if (isPending) {
			return <LinearProgress />;
		}

		if (error) {
			if (error instanceof HTTPError && error.response.status === 400) {
				const hasImplementedError = (
					error.data as ErrorData
				).propertyErrors.some(
					(error) => error.errorMessage === notImplementedMessage,
				);
				if (hasImplementedError) {
					return <SalesLossContentComingSoon />;
				}
			}
			return <ErrorMessage message="Unexpected error" />;
		}

		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 3,
					width: '1100px',
					maxWidth: '100%',
					mx: 'auto',
					py: 2,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 2,
						flexWrap: 'wrap',
					}}
				>
					<Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
						Sales Loss
					</Typography>
					{canExtractSalesLoss && (
						<Button
							variant="outlined"
							color="button"
							startIcon={<FontAwesomeIcon icon={faFileDownload} />}
							onClick={() =>
								downloadExtractSalesLoss(
									claimId,
									claim.claimReferenceNumber,
								).then(downloadFile)
							}
						>
							Extract Sales Loss
						</Button>
					)}
				</Box>

				{salesPeriodData?.intervalType === EIntervalType.Daily && (
					<DailySalesLossTable data={salesPeriodData} currency={currency} />
				)}
				{salesPeriodData?.intervalType === EIntervalType.Weekly && (
					<WeeklySalesLossTable data={salesPeriodData} currency={currency} />
				)}
				{salesPeriodData?.intervalType === EIntervalType.Monthly && (
					<MonthlySalesLossTable data={salesPeriodData} currency={currency} />
				)}

				<Box sx={{ mt: 3 }}>
					<Typography variant="h6" sx={{ fontWeight: 'bold' }}>
						Notes
					</Typography>
				</Box>
			</Box>
		);
	},
});
